import Component from 'core/Component';
import { on, off } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';
import { Event } from 'services/EventEmitter';

/**
 * This is a description of the StickyBar constructor function.
 * @class
 * @classdesc This component represents bar that could be sticked to the edge of the viewport.
 * This component work as extensible or as fallback variant for native CSS position: sticky.
 * @extends Component
 */
export default class StickyBar extends Component {
    /**
     * @typedef {Object} optionsObject
     * @property {object} classNames - CSS classes applied to the component
     * @property {string} classNames.active - CSS classes applied active state
     * @property {string} classNames.top - CSS classes applied to positioned top
     * @property {string} classNames.bottom - CSS classes applied to positioned bottom
     * @property {string} stickTo - what side of viewport will host panel. [top|bottom]
     * @property {bool} resizable - source file of resource
     */
    /**
     * @constructor
     * @param {object} element - DOM node of component
     * @param {optionsObject} options - Options
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            classNames: {
                active: 'm-sticked',
                top: 'm-top',
                bottom: 'm-bottom',
                hidden: 'h-hidden',
            },
            eventNamePrefix: null, // sticky bar identificator
            stickTo: 'bottom',
            _resizable: true,
            _scrollable: true,
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.bar = this.element.querySelector('[data-js-stickybar]');
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.isSticky = false;
        this.updateSticky();
    }

    /**
     * Move panel to stale state
     *
     * @returns {Boolean} - true or false
     */
    unstick() {
        if (!this.state.isSticky) {
            return false;
        }
        this.selectors.bar.classList.remove(this.options.classNames.active);
        this.state.isSticky = false;

        return true;
    }

    /**
     * Move panel to stick state
     *
     * @returns {Boolean} - true or false
     */
    stick() {
        if (this.state.isSticky) {
            return false;
        }
        this.selectors.bar.classList.add(this.options.classNames.active);
        this.state.isSticky = true;

        return true;
    }

    /**
     * Check if panel should be in sticky state
     * @return {Boolean} isSticky
     */
    isSticky() {
        const relativeScroll = this.element.getBoundingClientRect()[this.options.stickTo] - this.stickPoint;
        return this.options.stickTo === 'bottom' ? relativeScroll > 0 : relativeScroll < 0;
    }

    /**
     * Toggle sticky state between stale, sticky and vica versa
     */
    toggleStickyClass() {
        if (this.isSticky()) {
            this.setHeight();
            this.stick();
        } else {
            this.unstick();
            this.resetHeight();
        }
    }

    /**
     * Get point on viewport on what panel should be sticky
     */
    getStickyPoint() {
        this.stickPoint = this.options.stickTo === 'bottom' ? window.innerHeight : 0;
    }

    /**
     * Set height and initial CSS-classes to the holder that reserve space for stale state
     */
    setHeight() {
        this.selectors.bar.classList.add(this.options.classNames[this.options.stickTo]);
        this.element.style.height = `${this.selectors.bar.offsetHeight}px`;
    }

    /**
     * Destroy height and initial CSS-classes in case when component deinited
     */
    resetHeight() {
        this.selectors.bar.classList.remove(this.options.classNames[this.options.stickTo], this.options.classNames.active);
        this.element.style.height = 'initial';
    }

    /**
     * Initialize component DOM structure sizes and classes
     */
    updateSticky() {
        this.setHeight();
        this.getStickyPoint();
        this.toggleStickyClass();
    }

    /**
     * Handlers to onresize event
     */
    onResize() {
        this.updateSticky();
    }

    /**
     * Handlers to onscroll event
     */
    onScroll() {
        this.toggleStickyClass();
    }

    /**
     * Handler to 'stickybar.show' event
     */
    onShow() {
        this.element.classList.remove(this.options.classNames.hidden);
        this.toggleStickyClass();
    }

    /**
     * Handler to 'stickybar.hide' event
     */
    onHide() {
        this.element.classList.add(this.options.classNames.hidden);
        this.toggleStickyClass();
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('stickybar.show', this.element, this.onShow.bind(this));
        on('stickybar.hide', this.element, this.onHide.bind(this));

        if (this.options.eventNamePrefix) {
            Event.on(`${this.options.eventNamePrefix}.stickybar.show`, this.onShow, this);
            Event.on(`${this.options.eventNamePrefix}.stickybar.hide`, this.onHide, this);
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        this.resetHeight();
        off(this.element);

        if (this.options.eventNamePrefix) {
            Event.removeListener(`${this.options.eventNamePrefix}.stickybar.show`, this.onShow, this);
            Event.removeListener(`${this.options.eventNamePrefix}.stickybar.hide`, this.onHide, this);
        }
    }
}
